'use client';
/**
 * FaqSection.tsx — Client Component island
 * FAQ accordion with single-open mode and useState.
 * Cycle 76-B: extracted from HomepageClient.tsx as a standalone client island.
 *
 * Data: faq prop from page.tsx server component (content/homepage/index.json faq field).
 * JSON-LD FAQPage schema is rendered by the parent Server Component in page.tsx.
 */

import { useState } from 'react';

interface FaqItem {
  question?: string | null;
  answer?: string | null;
}

interface FaqData {
  eyebrow?: string | null;
  headline?: string | null;
  items?: (FaqItem | null)[] | null;
}

/* Hazel R2-B(4) formatted answers — JSX rich-text (bold/italic per Hazel spec).
   Order: Q1 What is PF → Q2 Different from → Q3 Teacher → Q4 Cancel → Q5 Beginner → Q6 Free */
const formattedAnswers: React.ReactNode[] = [
  /* Q1: What is Piano Fantasy? */
  <>
    <p><strong>Piano Fantasy is a piano-teaching platform built for serious adult pianists</strong> — the ones who care <em>how</em> they play, not just <em>what they play</em>. The teaching focuses on the mechanism layer: how the arm, wrist, and hand actually produce the sound you want.</p>
    <p>It is run by Lars Nelissen — <em>twenty-five years</em> of teaching, conservatory-trained in the Rubinstein tradition, working with adult pianists from beginner-returners to advanced.</p>
  </>,
  /* Q2: How is this different from private lessons or other online piano courses? */
  <>
    <p><strong>Against private lessons: a fraction of the cost, and faster feedback when you actually need it.</strong> A year of weekly private lessons typically runs around <em>$4,000</em> — roughly $100 an hour, forty weeks. And when you hit a wall mid-week — a fingering that won&apos;t work, a passage that resists — you don&apos;t wait seven days for the next lesson. You post in the community, upload a short video showing what&apos;s happening, and get an answer <em>within a day</em> — from me or from other pianists who&apos;ve been through the same wall.</p>
    <p><strong>Against other online piano courses: most are <em>courses only</em>.</strong> No live interaction, no personal time with the teacher, no path to ask the question that&apos;s actually blocking you. The Pro Membership includes both — the structured course material <em>and</em> the weekly contact. And on the teaching itself — <em>few methods actually teach a complete piano technique the way</em> <strong>the Super Fingers method</strong> <em>does</em>. It is the approach the great teachers have always used: how the arm, wrist, and hand coordinate to produce sound, taught as a complete system rather than piece-by-piece tips.</p>
  </>,
  /* Q3: Will this conflict with my in-person teacher? */
  <>
    <p><strong>No, it doesn&apos;t have to.</strong> Piano Fantasy can sit alongside in-person lessons very comfortably — <em>as long as your teacher is open to it</em>.</p>
    <p>That said, Piano Fantasy is designed to be used without a separate teacher too. So whether you have one or not, the teaching works.</p>
  </>,
  /* Q4: Can the Pro Membership be cancelled? */
  <>
    <p><strong>Yes — <em>any time</em>, from your account page.</strong> Try the first technique, do the first exercise, and if you don&apos;t feel the shift in how your hand sits at the keyboard: <em>full refund</em>, 30 days on the annual plan, 15 days on monthly.</p>
    <p>After that window, cancellation cadence depends on the plan you chose at signup. You can cancel at any point and the membership simply ends at the close of the current billing period. <em>No retention call, no friction.</em></p>
  </>,
  /* Q5: I'm completely new to the piano — is this for me? */
  <>
    <p>Yes — beginners join and do well here, and it can genuinely be a good time to start, since good technique and habits are easier to build from day one than to fix later.</p>
    <p>The one real condition: you should already be a little familiar with reading notes and able to work through a simple piece from a score on your own. Learning to read music from the very beginning isn&apos;t something this program teaches — the courses pick up one step after that.</p>
    <p>If you&apos;re not sure where that leaves you, the <a href="/piano-reading-level" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold-interactive-on-cream)', textDecoration: 'underline' }}>reading ladder<span className="sr-only"> (opens in a new tab)</span></a> gives you concrete ways to check, including a short score you can try in about a minute.</p>
  </>,
  /* Q6: Is there free content I can try first? */
  <>
    <p><strong>Yes — and most of what&apos;s worth reading is on the blog.</strong> That&apos;s where the longer pieces live: technique walk-throughs, repertoire notes, the kind of writing that takes more than a minute. There&apos;s also a <em>little bit</em> on YouTube, and the <a href="#newsletter" style={{ color: 'var(--gold-interactive-on-cream)', textDecoration: 'underline' }}>Piano Fantasy Minute newsletter</a> — twice a week in your inbox, free, no upsell inside the emails themselves.</p>
    <p>What the free path <em>can&apos;t</em> give you is <em>structure</em>. The blog is browse-it-when-you-want; the newsletter arrives in pieces. The Pro Membership is the same teaching laid out in <em>a coherent order</em>, with the exercises you actually do, the live lessons where you can ask the question that&apos;s blocking you, and personal guidance when you get stuck. The free content is <em>real</em> — it isn&apos;t a sample. The Pro Membership is a <em>different shape</em> of teaching, not a more-of-the-same upgrade.</p>
  </>,
];

export default function FaqSection({ faq }: { faq: FaqData }) {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(prev => prev === index ? null : index);
  };

  return (
    <section className="hp-faq" aria-labelledby="hp-faq-heading">
      <p className="eyebrow eyebrow--on-cream eyebrow--centered" aria-hidden="true">
        {faq.eyebrow ?? 'Good to know'}
      </p>
      <h2 id="hp-faq-heading">{faq.headline ?? 'What pianists ask before they explore further.'}</h2>

      <div className="hp-faq-list" role="list">
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        {(faq.items ?? []).map((item: any, i: number) => {
          const isOpen = openFaqIndex === i;
          const answer = formattedAnswers[i];
          return (
            <div
              key={i}
              className={`hp-faq-item${isOpen ? ' hp-faq-item--open' : ''}`}
              role="listitem"
            >
              <button
                className="hp-faq-question"
                aria-expanded={isOpen}
                aria-controls={`faq-answer-${i}`}
                onClick={() => toggleFaq(i)}
              >
                <span>{item?.question}</span>
                <span className="hp-faq-toggle" aria-hidden="true">
                  {isOpen ? '−' : '+'}
                </span>
              </button>
              <div
                id={`faq-answer-${i}`}
                className="hp-faq-answer"
                hidden={!isOpen}
              >
                <div className="hp-faq-answer-inner">
                  {answer}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <style>{`
        /* ── §8.5 FAQ Accordion — R2 rebuild, brand kit §14.7 ── */
        .hp-faq {
          background: var(--cream, #F4F0EA);
          padding: var(--section-pad-xl, 96px) var(--space-28, 28px);
          text-align: center;
        }
        .hp-faq h2 {
          color: var(--gold-on-cream, #9E7F36);
          max-width: 640px;
          margin: 0 auto var(--space-40, 40px);
        }
        .hp-faq-list {
          max-width: 720px;
          margin: 0 auto;
          text-align: left;
        }
        .hp-faq-item {
          border-bottom: 1px solid var(--border-gold, rgba(212,184,110,0.35));
        }
        .hp-faq-item:first-child {
          border-top: 1px solid var(--border-gold, rgba(212,184,110,0.35));
        }
        .hp-faq-item--open {
          border-left: 2px solid var(--gold-on-cream, #9E7F36);
          padding-left: var(--space-14, 14px);
        }
        .hp-faq-question {
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: var(--space-16, 16px);
          background: none;
          border: none;
          cursor: pointer;
          padding: var(--space-24, 24px) 0;
          font-family: var(--font-sans, 'Figtree', sans-serif);
          font-size: var(--text-body, 20px);
          font-weight: 600;
          color: var(--text-dark, #2A2233);
          text-align: left;
          transition: color var(--transition-fast, 150ms ease);
        }
        .hp-faq-question:hover,
        .hp-faq-item--open .hp-faq-question {
          color: var(--gold-interactive-on-cream, #7A5A28);
        }
        .hp-faq-toggle {
          font-size: 24px;
          font-weight: 300;
          line-height: 1;
          flex-shrink: 0;
          color: var(--eyebrow-on-cream, #7D6B3B);
          transition: color var(--transition-fast, 150ms ease);
        }
        .hp-faq-question:hover .hp-faq-toggle,
        .hp-faq-item--open .hp-faq-toggle {
          color: var(--gold-interactive-on-cream, #7A5A28);
        }
        .hp-faq-answer {
          padding-bottom: var(--space-24, 24px);
        }
        .hp-faq-answer[hidden] { display: none; }
        .hp-faq-answer-inner p {
          font-family: var(--font-sans, 'Figtree', sans-serif);
          font-size: var(--text-body, 20px);
          font-weight: 400;
          line-height: var(--lh-body, 1.6);
          color: var(--text-dark, #2A2233);
          margin: 0;
        }
        .hp-faq-answer-inner p + p { margin-top: var(--space-16, 16px); }
        @media (max-width: 768px) {
          .hp-faq { padding: var(--section-pad-mobile, 80px) var(--section-padding-x-mobile, 24px); }
          .hp-faq-item--open { border-left: none; padding-left: 0; }
        }
      `}</style>
    </section>
  );
}
